import { Badge, Button, Card, Checkbox, IconButton, IconTile, Input, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Database, File, FileText, Folder, HardDrive, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SharePointDrive, SharePointItem, SharePointSite } from "@/api";
import { getMyOnedriveOptions, listDriveItemsOptions, listSiteDrivesOptions, searchSitesOptions } from "@/api";
import { Text } from "@/components/Text";

/**
 * A focused SharePoint picker for the data-source create form: pick a site →
 * a document library (drive) → files or a folder. Returns the selection as
 * the config the backend expects. The old UI's full SharePointBrowser (1002
 * lines, OneDrive + folder-recursion + search) is a superset; this covers the
 * data-source flow.
 */

export interface SharePointSelection {
  site: SharePointSite;
  drive: SharePointDrive;
  /** Selected files (individual selection). */
  items: SharePointItem[];
  /** Selected folder (folder selection) + its path. */
  folder?: { item: SharePointItem; path: string };
}

type Step = "site" | "drive" | "items";

export function SharePointPicker({
  onSelect,
  onFiles,
}: Readonly<{
  /** Data sources: files or one folder, with its site + drive. */
  onSelect?: (sel: SharePointSelection | null) => void;
  /**
   * Files only (chat attachments): folders are for browsing, not picking, and
   * the user's own OneDrive is offered first.
   */
  onFiles?: (files: SharePointItem[]) => void;
}>) {
  const filesOnly = Boolean(onFiles);
  const { t } = useTranslation("dataSources");
  const [step, setStep] = useState<Step>("site");
  const [siteSearch, setSiteSearch] = useState("");
  const [site, setSite] = useState<SharePointSite | null>(null);
  const [drive, setDrive] = useState<SharePointDrive | null>(null);
  // Folder navigation stack (within the drive).
  const [folderStack, setFolderStack] = useState<SharePointItem[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<SharePointItem[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<SharePointItem | null>(null);

  const sites = useQuery(searchSitesOptions({ query: { q: siteSearch || "" } }));
  const oneDrive = useQuery({ ...getMyOnedriveOptions(), enabled: filesOnly });
  const drives = useQuery({
    ...listSiteDrivesOptions({ path: { site_id: (site?.id ?? "") as string } }),
    enabled: !!site,
  });
  const currentFolder = folderStack.at(-1);
  const items = useQuery({
    ...listDriveItemsOptions({
      path: { drive_id: (drive?.id ?? "") as string },
      query: currentFolder ? { folder_id: currentFolder.id } : {},
    }),
    enabled: !!drive,
  });

  const siteList = useMemo(() => sites.data ?? [], [sites.data]);
  const driveList = useMemo(() => drives.data ?? [], [drives.data]);
  const itemList = useMemo(() => items.data?.items ?? [], [items.data]);

  const reset = (to: Step) => {
    setStep(to);
    if (to === "site") {
      setSite(null);
      setDrive(null);
    }
    if (to === "drive") setDrive(null);
    setFolderStack([]);
    setSelectedFiles([]);
    setSelectedFolder(null);
    onSelect?.(null);
    onFiles?.([]);
  };

  const emit = (files: SharePointItem[], folder: SharePointItem | null) => {
    if (onFiles) {
      onFiles(files);
      return;
    }
    if (!onSelect || !site || !drive) return;
    if (folder) {
      const path = [...folderStack.map((f) => f.name), folder.name].join("/");
      onSelect({ site, drive, items: [], folder: { item: folder, path } });
    } else if (files.length > 0) {
      onSelect({ site, drive, items: files });
    } else {
      onSelect(null);
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        {/* Breadcrumb / back */}
        <div className="flex items-center gap-2">
          {step !== "site" && (
            <IconButton
              icon={<ArrowLeft />}
              aria-label={t("create.sharepoint.back")}
              variant="ghost"
              size="sm"
              onClick={() => reset(step === "items" && site ? "drive" : "site")}
            />
          )}
          <Text variant="body-sm" tone="secondary">
            {step === "site" && t("create.sharepoint.chooseSite")}
            {step === "drive" && (site?.display_name ?? t("create.sharepoint.chooseDrive"))}
            {step === "items" && (drive?.name ?? "")}
          </Text>
        </div>

        {step === "site" && (
          <>
            {filesOnly && oneDrive.data && (
              <PickerList
                items={[{ id: "onedrive", label: t("create.sharepoint.myOneDrive"), icon: <HardDrive size={16} /> }]}
                onPick={() => {
                  if (oneDrive.data) setDrive(oneDrive.data);
                  setStep("items");
                }}
              />
            )}
            <Input
              leadingIcon={<Search />}
              placeholder={t("create.sharepoint.searchSites")}
              aria-label={t("create.sharepoint.searchSites")}
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
            />
            {sites.isPending && <Spinner size="md" />}
            {!sites.isPending && siteList.length === 0 && (
              <Text variant="body-sm" tone="tertiary">
                {t("create.sharepoint.noSites")}
              </Text>
            )}
            {!sites.isPending && siteList.length > 0 && (
              <PickerList
                items={siteList.map((s) => ({ id: s.id, label: s.display_name, icon: <Database size={16} /> }))}
                onPick={(id) => {
                  setSite(siteList.find((s) => s.id === id) ?? null);
                  setStep("drive");
                }}
              />
            )}
          </>
        )}

        {step === "drive" &&
          (drives.isPending ? (
            <Spinner size="md" />
          ) : (
            <PickerList
              items={driveList.map((d) => ({ id: d.id, label: d.name, icon: <HardDrive size={16} /> }))}
              onPick={(id) => {
                setDrive(driveList.find((d) => d.id === id) ?? null);
                setStep("items");
              }}
            />
          ))}

        {step === "items" && (
          <>
            {folderStack.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setFolderStack((s) => s.slice(0, -1))}>
                {t("create.sharepoint.up")}
              </Button>
            )}
            {items.isPending && <Spinner size="md" />}
            {!items.isPending && itemList.length === 0 && (
              <Text variant="body-sm" tone="tertiary">
                {t("create.sharepoint.emptyFolder")}
              </Text>
            )}
            {!items.isPending && itemList.length > 0 && (
              <ul className="flex max-h-72 flex-col divide-y divide-border-subtle overflow-y-auto">
                {itemList.map((item) => {
                  const isFolder = item.is_folder;
                  const checked = selectedFolder?.id === item.id || selectedFiles.some((f) => f.id === item.id);
                  return (
                    <li key={item.id} className="flex items-center gap-3 py-2">
                      {filesOnly && isFolder ? (
                        <span className="w-4 shrink-0" />
                      ) : (
                        <Checkbox
                          aria-label={item.name}
                          checked={checked}
                          onChange={(on) => {
                            if (isFolder) {
                              const next = on ? item : null;
                              setSelectedFolder(next);
                              setSelectedFiles([]);
                              emit([], next);
                            } else {
                              const next = on
                                ? [...selectedFiles, item]
                                : selectedFiles.filter((f) => f.id !== item.id);
                              setSelectedFiles(next);
                              setSelectedFolder(null);
                              emit(next, null);
                            }
                          }}
                        />
                      )}
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                        onClick={() => isFolder && setFolderStack((s) => [...s, item])}
                        disabled={!isFolder}
                      >
                        <span className="text-content-tertiary">
                          {isFolder ? <Folder size={16} /> : <FileIcon name={item.name} />}
                        </span>
                        <Text variant="body-sm" className="truncate">
                          {item.name}
                        </Text>
                        {isFolder && <ChevronRight size={14} className="text-content-tertiary" />}
                      </button>
                      {item.child_count != null && isFolder && (
                        <Badge variant="neutral" size="compact">
                          {t("create.sharepoint.itemCount", { count: item.child_count })}
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {(selectedFiles.length > 0 || selectedFolder) && (
              <Text variant="caption" tone="secondary">
                {selectedFolder
                  ? t("create.sharepoint.folderSelected", { name: selectedFolder.name })
                  : t("create.sharepoint.filesSelected", { count: selectedFiles.length })}
              </Text>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function PickerList({
  items,
  onPick,
}: Readonly<{ items: Array<{ id: string; label: string; icon: React.ReactNode }>; onPick: (id: string) => void }>) {
  return (
    <ul className="flex max-h-72 flex-col divide-y divide-border-subtle overflow-y-auto">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3 rounded py-2 text-left btn-focus-ring"
            onClick={() => onPick(item.id)}
          >
            <IconTile>{item.icon}</IconTile>
            <Text variant="body-sm" className="flex-1 truncate">
              {item.label}
            </Text>
            <ChevronRight size={14} className="text-content-tertiary" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function FileIcon({ name }: Readonly<{ name: string }>) {
  const ext = name.split(".").pop()?.toLowerCase();
  return ["pdf", "doc", "docx", "txt", "md"].includes(ext ?? "") ? <FileText size={16} /> : <File size={16} />;
}
